import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import {
  type InstrumentType,
  isInstrumentType,
  INSTRUMENT_OPTIONS,
} from "../lib/instrumentEngine";
import { getPracticeSurface, type PracticeSurface } from "../lib/practiceSurface";

const STORAGE_KEY = "bordona-instrument";
const SESSION_DAY_KEY = "bordona-instrument-session-day";

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function readStoredInstrument(): InstrumentType {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && isInstrumentType(saved)) return saved;
  } catch {}
  return "guitar";
}

function readSessionDay(): string | null {
  try {
    return localStorage.getItem(SESSION_DAY_KEY);
  } catch {
    return null;
  }
}

interface InstrumentContextValue {
  instrument: InstrumentType;
  surface: PracticeSurface;
  setInstrument: (type: InstrumentType) => void;
  /** Elige instrumento para la sesión de hoy y va al estudio. */
  chooseInstrumentForToday: (type: InstrumentType) => void;
  needsInstrumentPick: boolean;
  requestInstrumentPick: () => void;
  toggleInstrument: () => void;
}

const InstrumentContext = createContext<InstrumentContextValue>({
  instrument: "guitar",
  surface: "fretboard",
  setInstrument: () => {},
  chooseInstrumentForToday: () => {},
  needsInstrumentPick: false,
  requestInstrumentPick: () => {},
  toggleInstrument: () => {},
});

export const InstrumentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [instrument, setInstrumentState] = useState<InstrumentType>(readStoredInstrument);
  const [sessionDay, setSessionDay] = useState<string | null>(readSessionDay);

  const needsInstrumentPick = sessionDay !== todayKey();

  const persist = useCallback((type: InstrumentType, day: string) => {
    setInstrumentState(type);
    setSessionDay(day);
    try {
      localStorage.setItem(STORAGE_KEY, type);
      localStorage.setItem(SESSION_DAY_KEY, day);
    } catch {}
  }, []);

  const setInstrument = useCallback((type: InstrumentType) => {
    persist(type, todayKey());
  }, [persist]);

  const chooseInstrumentForToday = useCallback((type: InstrumentType) => {
    persist(type, todayKey());
  }, [persist]);

  const requestInstrumentPick = useCallback(() => {
    setSessionDay(null);
    try {
      localStorage.removeItem(SESSION_DAY_KEY);
    } catch {}
  }, []);

  const toggleInstrument = useCallback(() => {
    setInstrumentState((prev) => {
      const idx = INSTRUMENT_OPTIONS.findIndex((o) => o.id === prev);
      const next = INSTRUMENT_OPTIONS[(idx + 1) % INSTRUMENT_OPTIONS.length].id;
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {}
      return next;
    });
  }, []);

  const surface = useMemo(() => getPracticeSurface(instrument), [instrument]);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue && isInstrumentType(e.newValue)) {
        setInstrumentState(e.newValue);
      }
      if (e.key === SESSION_DAY_KEY) {
        setSessionDay(e.newValue);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const value = useMemo(
    () => ({
      instrument,
      surface,
      setInstrument,
      chooseInstrumentForToday,
      needsInstrumentPick,
      requestInstrumentPick,
      toggleInstrument,
    }),
    [
      instrument,
      surface,
      setInstrument,
      chooseInstrumentForToday,
      needsInstrumentPick,
      requestInstrumentPick,
      toggleInstrument,
    ]
  );

  return (
    <InstrumentContext.Provider value={value}>{children}</InstrumentContext.Provider>
  );
};

export function useInstrument(): InstrumentContextValue {
  return useContext(InstrumentContext);
}
